import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { sessionEvents } from './sessionEvents'
import { tokenStorage } from './tokenStorage'

const instance = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL || '',
})

interface FailedQueueItem {
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}

let isRefreshing = false
let failedQueue: FailedQueueItem[] = []

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

instance.interceptors.request.use((config) => {
  // Prevent hitting APIs if the session is already marked as expired
  if (sessionEvents.isExpired()) {
    return Promise.reject(new axios.Cancel('Session expired. Request aborted.'))
  }

  const token = tokenStorage.getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  // Required by almost every authenticated endpoint per Samarth.postman_collection.json.
  const apiKey = process.env.REACT_APP_API_KEY
  if (apiKey) {
    config.headers['X-API-Key'] = apiKey
  }

  return config
})

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined

    if (!error.response || error.response.status !== 401 || !originalRequest) {
      return Promise.reject(error)
    }

    const url = originalRequest.url || ''
    // Do not attempt token refresh on login, refresh-token, or forgot-password endpoints
    if (
      url.includes('/api/v1/login') ||
      url.includes('/api/v1/refresh-token') ||
      url.includes('/api/v1/forgot-password')
    ) {
      return Promise.reject(error)
    }

    if (originalRequest._retry) {
      return Promise.reject(error)
    }

    const refreshToken = tokenStorage.getRefreshToken()
    if (!refreshToken) {
      tokenStorage.clear()
      sessionEvents.notifySessionExpired()
      return Promise.reject(error)
    }

    if (isRefreshing) {
      // If a refresh is already in flight, queue this request to retry when the new token arrives
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      })
        .then((newToken) => {
          originalRequest._retry = true
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return instance(originalRequest)
        })
        .catch((err) => Promise.reject(err))
    }

    originalRequest._retry = true
    isRefreshing = true

    try {
      const baseURL = process.env.REACT_APP_BASE_URL || ''
      const apiKey = process.env.REACT_APP_API_KEY

      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (apiKey) {
        headers['X-API-Key'] = apiKey
      }

      // Plain axios call bypasses interceptors to avoid infinite loops
      const response = await axios.post<{
        access_token: string
        refresh_token: string
        expires_in: number
      }>(`${baseURL}/api/v1/refresh-token`, { refresh_token: refreshToken }, { headers })

      const { access_token, refresh_token: newRefreshToken, expires_in } = response.data

      if (!access_token) {
        throw new Error('No access token returned from refresh endpoint.')
      }

      tokenStorage.save(access_token, newRefreshToken || refreshToken, expires_in || 3600)

      processQueue(null, access_token)

      originalRequest.headers.Authorization = `Bearer ${access_token}`
      return instance(originalRequest)
    } catch (refreshErr) {
      processQueue(refreshErr as Error, null)
      tokenStorage.clear()
      sessionEvents.notifySessionExpired()
      return Promise.reject(refreshErr)
    } finally {
      isRefreshing = false
    }
  },
)

export default instance
